/*
  Shared wishlist engine. Loaded once from layout/theme.liquid so it always
  executes via a normal browser HTML parse — critically, on pages like
  /collections/all where the entire product grid is populated later via
  `container.innerHTML = ...` (see main-collection-product-grid.liquid's
  navigateWithSectionRender). Browsers never execute <script> tags that
  arrive through an innerHTML assignment, so this logic used to live inline
  inside snippets/wishlist-button.liquid (rendered once per product card)
  and simply never ran on any page whose first product render came from
  that innerHTML swap — window.initWishlistButtons stayed undefined, so no
  wishlist button ever got a click listener, and the click fell through to
  the product card's own <a href> instead.

  window.SYC_WISHLIST_CUSTOMER is set from Liquid in theme.liquid, right
  before this file is loaded.
*/
(function () {
  if (window.__wishlistCountsInit) return;
  window.__wishlistCountsInit = true;

  const MAX_BATCH = 50;

  function updateWishlistLabel(btn) {
    const label = btn.querySelector('.wishlist-label');
    if (!label) return;
    label.textContent = btn.classList.contains('active')
      ? (btn.dataset.labelActive   || 'In Wishlist')
      : (btn.dataset.labelInactive || 'Add to Wishlist');
  }

  function chunkIds(ids) {
    const unique = Array.from(new Set(ids));
    const batches = [];
    for (let i = 0; i < unique.length; i += MAX_BATCH) {
      batches.push(unique.slice(i, i + MAX_BATCH));
    }
    return batches;
  }

  const customerInfo  = window.SYC_WISHLIST_CUSTOMER || {};
  const customerId    = customerInfo.id || null;
  const customerEmail = customerInfo.email || 'guest@example.com';

  const API_BASE_URL = '/apps/wishlist';

  // Caches handle → id (+ brand/catalog info) so Wishlist.liquid can resolve
  // handles without a network round-trip. Reads straight off each button's
  // own data-* attributes (already rendered server-side per instance), so
  // it works the same whether the button arrived via a normal page parse or
  // an AJAX-swapped grid. Idempotent — safe to call on every re-init.
  function cacheWishlistHandle(btn) {
    const id     = btn.dataset.id;
    const handle = btn.dataset.handle;
    if (!id || !handle) return;
    const brand         = btn.dataset.brand || '';
    const mode          = btn.dataset.mode || 'product';
    const title         = btn.dataset.title || '';
    const imageUrl      = btn.dataset.image || '';
    const catalogLiveTs = parseInt(btn.dataset.catalogLiveTs, 10) || 0;
    const catalogId     = parseInt(btn.dataset.catalogId, 10) || 0;
    const catalogType   = btn.dataset.catalogType || '';

    try {
      const raw   = localStorage.getItem('wl-handle-cache-v1');
      const cache = raw ? JSON.parse(raw) : { products: {}, collections: {} };
      if (!cache.products)    cache.products    = {};
      if (!cache.collections) cache.collections = {};
      if (mode === 'collection') {
        cache.collections[id] = { handle: handle };
        if (title)    { try { localStorage.setItem('wl-collection-title-' + id, title); } catch (_te) {} }
        if (imageUrl) { try { localStorage.setItem('wl-collection-image-' + id, imageUrl); } catch (_ie) {} }
        if (catalogLiveTs) { try { localStorage.setItem('wl-catalog-live-' + id, String(catalogLiveTs)); } catch (_ce) {} }
        if (catalogType)   { try { localStorage.setItem('wl-catalog-type-' + id, catalogType); } catch (_ye) {} }
      } else {
        cache.products[id] = { handle: handle, brand: brand, catalogLiveTs: catalogLiveTs, catalogId: catalogId };
      }
      localStorage.setItem('wl-handle-cache-v1', JSON.stringify(cache));
    } catch (_e) {}
  }

  // ── Auth modal + intent preservation ──────────────────────────────
  // Logged-out heart clicks open this modal instead of jumping straight
  // to /account/login. The intended product is stashed in localStorage
  // so that after Shopify's customer auth flow returns the user to the
  // same page, we can finish the add silently. Key + TTL guard prevent
  // stale intents from firing later.
  const PENDING_KEY = 'syc:pendingWishlistAdd';
  const PENDING_TTL_MS = 15 * 60 * 1000; // 15 min

  function stashPendingWishlist(btn) {
    try {
      const productId = btn.getAttribute('data-product-id');
      const collectionId = btn.getAttribute('data-collection-id');
      if (!productId && !collectionId) return;
      localStorage.setItem(PENDING_KEY, JSON.stringify({
        productId: productId || null,
        collectionId: collectionId || null,
        returnUrl: window.location.pathname + window.location.search,
        ts: Date.now()
      }));
    } catch (e) { /* localStorage disabled — no-op, user will just lose intent */ }
  }

  function readPendingWishlist() {
    try {
      const raw = localStorage.getItem(PENDING_KEY);
      if (!raw) return null;
      const data = JSON.parse(raw);
      if (!data || (Date.now() - (data.ts || 0)) > PENDING_TTL_MS) {
        localStorage.removeItem(PENDING_KEY);
        return null;
      }
      return data;
    } catch (e) { return null; }
  }

  function clearPendingWishlist() {
    try { localStorage.removeItem(PENDING_KEY); } catch (e) {}
  }

  // Trigger element for focus return on close. Tracked in module scope so
  // close handlers (backdrop, X, Escape) can all restore focus to the heart
  // that opened the modal.
  let _wishlistAuthTrigger = null;

  function ensureWishlistAuthModal() {
    if (document.getElementById('syc-wishlist-auth-modal')) return;

    // Hero image for the auth panel — Shopify CDN-hosted. Swap the URL here
    // (or move to `settings.wishlist_modal_image` later) to change it.
    const heroImage = 'https://cdn.shopify.com/s/files/1/0703/0652/9510/files/summer_time_woman_laughing.png?v=1778560991';

    const modal = document.createElement('div');
    modal.id = 'syc-wishlist-auth-modal';
    modal.setAttribute('role', 'dialog');
    modal.setAttribute('aria-modal', 'true');
    modal.setAttribute('aria-labelledby', 'syc-wishlist-auth-title');
    modal.setAttribute('aria-hidden', 'true');
    modal.innerHTML = ''
      + '<div class="syc-wishlist-auth__panel" tabindex="-1" role="document">'
      +   '<button class="syc-wishlist-auth__close" type="button" data-close aria-label="Close">'
      +     '<svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true" focusable="false"><path d="M1 1L15 15M15 1L1 15" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" pointer-events="none"/></svg>'
      +   '</button>'
      +   '<div class="syc-wishlist-auth__media" aria-hidden="true">'
      +     (heroImage ? '<img src="' + heroImage + '" alt="">' : '')
      +   '</div>'
      +   '<div class="syc-wishlist-auth__content">'
      +     '<h2 class="syc-wishlist-auth__title" id="syc-wishlist-auth-title">Save to your wishlist</h2>'
      +     '<p class="syc-wishlist-auth__body">Log in or create an account to save items you love. We’ll add this piece to your wishlist as soon as you’re signed in.</p>'
      +     '<div class="syc-wishlist-auth__actions">'
      +       '<a href="#" class="syc-wishlist-auth__btn syc-wishlist-auth__btn--primary" data-auth="login">Log in</a>'
      +       '<a href="#" class="syc-wishlist-auth__btn syc-wishlist-auth__btn--secondary" data-auth="register">Create account</a>'
      +     '</div>'
      +   '</div>'
      + '</div>';

    const style = document.createElement('style');
    // The backdrop is the modal container's own background — guarantees the
    // overlay covers the full viewport, no separate element to misalign or
    // get accidentally hidden. z-index 2147483000 clears the sticky header
    // (~999), mobile filter sheet (200), and any third-party widgets.
    style.textContent = ''
      + '#syc-wishlist-auth-modal{position:fixed;inset:0;z-index:2147483000;display:none;align-items:center;justify-content:center;padding:1rem;background:rgba(0,0,0,0.5);font-family:var(--font-body-family,"Inter",sans-serif);}'
      + '#syc-wishlist-auth-modal.is-open{display:flex;}'
      + '.syc-wishlist-auth__panel{position:relative;background:#FAEEDA;color:#2C2C2A;width:100%;max-width:720px;display:grid;grid-template-columns:1fr 1fr;box-shadow:0 12px 40px rgba(0,0,0,0.25);overflow:hidden;}'
      + '.syc-wishlist-auth__close{position:absolute;top:0.5rem;right:0.5rem;z-index:2;background:rgba(250,238,218,0.9);border:0;color:#2C2C2A;cursor:pointer;width:2rem;height:2rem;display:flex;align-items:center;justify-content:center;border-radius:50%;}'
      + '.syc-wishlist-auth__close:hover{background:#FAEEDA;}'
      + '.syc-wishlist-auth__media{position:relative;background:#2C2C2A;min-height:220px;}'
      + '.syc-wishlist-auth__media img{position:absolute;inset:0;width:100%;height:100%;object-fit:cover;display:block;}'
      + '.syc-wishlist-auth__content{padding:2.25rem 1.75rem 1.75rem;display:flex;flex-direction:column;justify-content:center;}'
      + '.syc-wishlist-auth__title{margin:0 0 0.6rem;font-size:1.25rem;letter-spacing:0.06em;text-transform:uppercase;font-weight:700;line-height:1.2;}'
      + '.syc-wishlist-auth__body{margin:0 0 1.5rem;font-size:0.9375rem;line-height:1.5;color:rgba(44,44,42,0.85);}'
      + '.syc-wishlist-auth__actions{display:flex;flex-direction:column;gap:0.6rem;}'
      + '.syc-wishlist-auth__btn{display:flex;align-items:center;justify-content:center;height:3rem;padding:0 1rem;text-decoration:none;font-size:0.8125rem;letter-spacing:0.12em;text-transform:uppercase;font-weight:600;}'
      + '.syc-wishlist-auth__btn--primary{background:#2C2C2A;color:#FAEEDA;}'
      + '.syc-wishlist-auth__btn--primary:hover{background:#854F0B;}'
      + '.syc-wishlist-auth__btn--secondary{background:transparent;color:#2C2C2A;border:1px solid #2C2C2A;}'
      + '.syc-wishlist-auth__btn--secondary:hover{background:#2C2C2A;color:#FAEEDA;}'
      + '@media (max-width:600px){.syc-wishlist-auth__panel{grid-template-columns:1fr;max-width:420px;}.syc-wishlist-auth__media{min-height:0;aspect-ratio:16/9;}.syc-wishlist-auth__content{padding:1.75rem 1.5rem;}}';

    document.head.appendChild(style);
    document.body.appendChild(modal);

    // Close interactions:
    //   - X button: any descendant of [data-close] (using closest() so SVG path clicks resolve)
    //   - Click outside the panel: the backdrop is the modal container itself,
    //     so a click that doesn't land inside .syc-wishlist-auth__panel is on the backdrop
    //   - Escape key
    modal.addEventListener('click', function (e) {
      if (e.target.closest('[data-close]')) {
        closeWishlistAuthModal();
      } else if (!e.target.closest('.syc-wishlist-auth__panel')) {
        closeWishlistAuthModal();
      }
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && modal.classList.contains('is-open')) closeWishlistAuthModal();
    });
  }

  function openWishlistAuthModal(btn) {
    ensureWishlistAuthModal();
    stashPendingWishlist(btn);
    _wishlistAuthTrigger = btn;

    const modal = document.getElementById('syc-wishlist-auth-modal');
    const returnUrl = window.location.pathname + window.location.search;
    const encoded = encodeURIComponent(returnUrl);
    // Shopify's standard customer auth — `return_url` brings them back here.
    modal.querySelector('[data-auth="login"]').href = '/account/login?return_url=' + encoded;
    modal.querySelector('[data-auth="register"]').href = '/account/register?return_url=' + encoded;

    modal.classList.add('is-open');
    modal.setAttribute('aria-hidden', 'false');
    // Focus the close button (first focusable inside the modal) so keyboard
    // users can dismiss with Enter/Space immediately.
    const closeBtn = modal.querySelector('.syc-wishlist-auth__close');
    if (closeBtn) closeBtn.focus();
    document.body.style.overflow = 'hidden';
  }

  function closeWishlistAuthModal() {
    const modal = document.getElementById('syc-wishlist-auth-modal');
    if (!modal) return;
    modal.classList.remove('is-open');
    modal.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    // Return focus to the heart that opened the modal.
    if (_wishlistAuthTrigger && typeof _wishlistAuthTrigger.focus === 'function') {
      _wishlistAuthTrigger.focus();
    }
    _wishlistAuthTrigger = null;
  }

  // After Shopify auth returns the user, finish the add they started.
  // Runs once per page load if customer is signed in and a fresh intent exists.
  function resumePendingWishlist() {
    if (!customerId) return;
    const pending = readPendingWishlist();
    if (!pending) return;

    // Identity now comes from the App Proxy signature (logged_in_customer_id).
    // No customer_id / customer_name / customer_email in body — same-origin too.
    const body = {};
    if (pending.productId) body.shopify_product_id = pending.productId;
    else if (pending.collectionId) body.shopify_collection_id = pending.collectionId;
    else { clearPendingWishlist(); return; }

    fetch(API_BASE_URL + '/create.php', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    })
    .then(r => r.json().catch(() => null))
    .then(data => {
      // Reflect new state on any matching button currently on the page.
      const sel = pending.productId
        ? '.wishlist-btn[data-product-id="' + pending.productId + '"]'
        : '.wishlist-btn[data-collection-id="' + pending.collectionId + '"]';
      document.querySelectorAll(sel).forEach(b => {
        b.classList.add('active');
        updateWishlistLabel(b);
      });
      if (pending.productId) {
        try { refreshProductWishlistCount(pending.productId, 1); } catch (e) {}
      } else if (pending.collectionId) {
        try { refreshCollectionWishlistCount(pending.collectionId, 1); } catch (e) {}
      }
    })
    .catch(() => {})
    .finally(clearPendingWishlist);
  }

  let lastProductCountTs = 0;
  let lastCollectionCountTs = 0;
  const COUNT_COOLDOWN_MS = 5000; // throttle repeat calls
  let countsTimer = null; // debounce count fetches
  const MAX_COUNTS_REFRESHES = 3; // cap total count refreshes per page
  let countsRefreshCount = 0;

  function scheduleCountsRefresh(force = false, delayMs = 600) {
    if (countsRefreshCount >= MAX_COUNTS_REFRESHES) {
      return;
    }
    if (force) {
      window.__wishlistProdIdsKey = '';
      window.__wishlistCollIdsKey = '';
      window.__wishlistProdCountsInFlight = false;
      window.__wishlistCollCountsInFlight = false;
      lastProductCountTs = 0;
      lastCollectionCountTs = 0;
    }
    if (countsTimer) clearTimeout(countsTimer);
    countsTimer = setTimeout(() => {
      countsTimer = null;
      countsRefreshCount++;
      loadProductWishlistCounts();
      loadCollectionWishlistCounts();
    }, delayMs);
  }

  // Function to load product wishlist counts
  function loadProductWishlistCounts() {
    // Only grab product counters, not collection ones
    const productCountElements = document.querySelectorAll('.wishlist-count[data-product-id]');
    if (productCountElements.length === 0) return;

    const productIds = Array.from(productCountElements)
      .map(el => el.getAttribute('data-product-id'))
      .filter(id => id && id !== 'null' && id !== 'undefined');

    if (productIds.length === 0) return;

    // Avoid duplicate count calls for same ID set
    window.__wishlistProdIdsKey = window.__wishlistProdIdsKey || '';
    const prodKey = productIds.slice().sort().join(',');
    if (prodKey === window.__wishlistProdIdsKey) return;
    window.__wishlistProdIdsKey = prodKey;

    // Prevent overlapping fetches
    if (window.__wishlistProdCountsInFlight) return;
    window.__wishlistProdCountsInFlight = true;

    // Cooldown guard
    const nowTs = Date.now();
    if (nowTs - lastProductCountTs < COUNT_COOLDOWN_MS) {
      window.__wishlistProdCountsInFlight = false;
      return;
    }
    lastProductCountTs = nowTs;

    const batches = chunkIds(productIds);

    const requests = batches.map(batch =>
      fetch(`${API_BASE_URL}/products-counts.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ product_ids: batch })
      })
      .then(response => {
        if (response.status === 429) return null;
        return response.json();
      })
      .catch(() => null)
    );

    Promise.all(requests).then(results => {
      results.filter(Boolean).forEach(data => {
        if (data.success && data.counts) {
          data.counts.forEach(item => {
            const countElement = document.querySelector(`.wishlist-count[data-product-id="${item.product_id}"] .count-number`);
            if (countElement) {
              countElement.textContent = item.wishlist_count || '';
              countElement.parentElement.classList.remove('loading');
              countElement.parentElement.classList.add('loaded');
            }
          });
        }
      });
    }).finally(() => {
      window.__wishlistProdCountsInFlight = false;
    });
  }

  // Function to load collection wishlist counts
  function loadCollectionWishlistCounts() {
    // Only grab collection counters, not product ones
    const collectionCountElements = document.querySelectorAll('.wishlist-count[data-collection-id]');
    if (collectionCountElements.length === 0) return;

    const collectionIds = Array.from(collectionCountElements)
      .map(el => el.getAttribute('data-collection-id'))
      .filter(id => id && id !== 'null' && id !== 'undefined');

    if (collectionIds.length === 0) return;

    // Avoid duplicate count calls for same ID set
    window.__wishlistCollIdsKey = window.__wishlistCollIdsKey || '';
    const collKey = collectionIds.slice().sort().join(',');
    if (collKey === window.__wishlistCollIdsKey) return;
    window.__wishlistCollIdsKey = collKey;

    // Prevent overlapping fetches
    if (window.__wishlistCollCountsInFlight) return;
    window.__wishlistCollCountsInFlight = true;

    // Cooldown guard
    const nowTs = Date.now();
    if (nowTs - lastCollectionCountTs < COUNT_COOLDOWN_MS) {
      window.__wishlistCollCountsInFlight = false;
      return;
    }
    lastCollectionCountTs = nowTs;

    const batches = chunkIds(collectionIds);

    const requests = batches.map(batch =>
      fetch(`${API_BASE_URL}/collections-counts.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ collection_ids: batch })
      })
      .then(response => {
        if (response.status === 429) return null;
        return response.json();
      })
      .catch(() => null)
    );

    Promise.all(requests).then(results => {
      results.filter(Boolean).forEach(data => {
        if (data.success && data.counts) {
          data.counts.forEach(item => {
            const countElement = document.querySelector(`.wishlist-count[data-collection-id="${item.collection_id}"] .count-number`);
            if (countElement) {
              countElement.textContent = item.wishlist_count || '';
              countElement.parentElement.classList.remove('loading');
              countElement.parentElement.classList.add('loaded');
            }
          });
        }
      });
    }).finally(() => {
      window.__wishlistCollCountsInFlight = false;
    });
  }

  // Public init to wire up wishlist buttons (can be called again for dynamically added content)
  window.initWishlistButtons = function initWishlistButtons() {
    // Force active-state recheck when new buttons appear (e.g., related products)
    window.wishlistDataLoaded = false;

    // Adjust layout (only for buttons without explicit display_style)
    document.querySelectorAll('.wishlist-btn').forEach(btn => {
      if (btn.dataset.style === 'button' || btn.dataset.style === 'icon') return;

      const parentCard = btn.closest('.product-card, .grid__item');
      const inProductPage = document.body.classList.contains('template-product');

      if (inProductPage) {
          btn.classList.add('button-style');
          if (!btn.querySelector('.wishlist-label')) {
          const span = document.createElement('span');
          span.classList.add('wishlist-label');
          span.textContent = 'Add to Wishlist';
          btn.appendChild(span);
          }
      } else if (parentCard) {
          btn.classList.remove('button-style');
          const label = btn.querySelector('.wishlist-label');
          if (label) label.remove();
      }
    });

    // Get all wishlist buttons (show to all users)
    const wishlistButtons = Array.from(document.querySelectorAll(".wishlist-btn"));

    // Only proceed if wishlist buttons exist
    if (wishlistButtons.length === 0) {
      return; // Exit early if no wishlist buttons
    }

    // Cache handle → id mapping for every button currently on the page —
    // covers both a normal page parse and buttons that just arrived via an
    // AJAX innerHTML swap (this used to run per-card inline and silently
    // never fired for the latter case).
    wishlistButtons.forEach(cacheWishlistHandle);

    // Rate limiting for button clicks
    let lastClickTime = 0;
    const CLICK_COOLDOWN = 500; // 0.5 seconds between clicks (reduced for better UX)

    wishlistButtons.forEach(btn => {
      // Avoid duplicate listeners on re-init
      if (btn.dataset.wishlistBound === 'true') return;
      btn.dataset.wishlistBound = 'true';

      btn.addEventListener("click", function(e) {
        e.preventDefault();

        // Not logged in → open auth modal, preserve intent for post-auth resume.
        if (!customerId) {
          openWishlistAuthModal(btn);
          return;
        }

        // Rate limiting: prevent rapid clicking
        const now = Date.now();
        if (now - lastClickTime < CLICK_COOLDOWN) {
          return;
        }
        lastClickTime = now;

        // Prevent multiple clicks
        if (btn.classList.contains('loading')) {
          return;
        }

        // Add loading state
        btn.classList.add('loading');

        // Detect type (product vs collection)
        const productId = btn.getAttribute("data-product-id");
        const collectionId = btn.getAttribute("data-collection-id");
        const isProduct = !!productId;
        const isCollection = !!collectionId;

        // Identity comes from App Proxy signature — no customer_* in body.
        const requestData = {};
        if (isProduct) {
          requestData.shopify_product_id = productId;
          requestData.handle = btn.getAttribute('data-handle') || '';
          requestData.brand  = btn.getAttribute('data-brand')  || '';
        } else if (isCollection) {
          requestData.shopify_collection_id = collectionId;
          requestData.handle = btn.getAttribute('data-collection-handle') || '';
        }

        // Check if already in wishlist (active state)
        const isInWishlist = btn.classList.contains("active");

        // Call SEUYCO wishlist API. Both create.php and remove.php are POST —
        // bodies on DELETE get stripped by some proxies, so we standardize.
        const apiEndpoint = isInWishlist ? `${API_BASE_URL}/remove.php` : `${API_BASE_URL}/create.php`;

        fetch(apiEndpoint, {
          method: 'POST',
          headers: {
            "Content-Type": "application/json"
          },
          body: JSON.stringify(requestData)
        })
        .then(response => response.json())
        .then(data => {
          if (data.success) {
            // Toggle active class
            if (isInWishlist) {
              btn.classList.remove("active");
            } else {
              btn.classList.add("active");
            }
            updateWishlistLabel(btn);

            // Analytics
            try { trackUniversalWishlist(btn); } catch (e) {}

            // Update the wishlist count
            if (isProduct && productId) {
              refreshProductWishlistCount(productId, isInWishlist ? -1 : 1);
            } else if (isCollection && collectionId) {
              refreshCollectionWishlistCount(collectionId, isInWishlist ? -1 : 1);
            }
          } else {
            // Handle error

            // If already in wishlist, mark as active
            if (data.message && data.message.includes('already in your wishlist')) {
              btn.classList.add("active");
            } else {
              alert(`Something went wrong. Please refresh the page.`);
            }
          }
        })
        .catch(error => {
          alert("Wishlist process is in progress. Please wait…");
        })
        .finally(() => {
          // Remove loading state
          btn.classList.remove('loading');
        });
      });
    });

    // Rate limiting protection
    let lastWishlistCheck = 0;
    let isCheckingWishlist = false;
    const WISHLIST_CHECK_COOLDOWN = 5000; // 5 seconds between checks (reduced for better UX)

    function checkExistingWishlist() {
      // Prevent multiple simultaneous calls
      if (isCheckingWishlist) {
        return;
      }

      // Rate limiting: don't check too frequently (but allow first call)
      const now = Date.now();
      if (lastWishlistCheck > 0 && now - lastWishlistCheck < WISHLIST_CHECK_COOLDOWN) {
        return;
      }

      // Additional check: if we already have wishlist data, don't fetch again
      if (window.wishlistDataLoaded) {
        return;
      }

      isCheckingWishlist = true;
      lastWishlistCheck = now;

      // Get all product/collection IDs on the current page
      const productIds = [];
      const collectionIds = [];
      wishlistButtons.forEach(btn => {
        const pid = btn.getAttribute("data-product-id");
        const cid = btn.getAttribute("data-collection-id");
        if (pid) productIds.push(pid);
        if (cid) collectionIds.push(cid);
      });

      // Add delay to prevent rapid API calls
      setTimeout(() => {

        const promises = [];

        // Products active-state
        if (customerId && productIds.length > 0) {
          promises.push(
            fetch(`${API_BASE_URL}/customer.php`, {
              method: "GET",
              headers: { "Content-Type": "application/json" }
            })
            .then(response => {
              if (response.status === 429) return null;
              return response.json();
            })
            .then(data => {
              if (!data) return;
              if (data && data.success && data.wishlists) {
                const wishlistProductIds = data.wishlists.map(item => item.shopify_product_id && item.shopify_product_id.toString());
                wishlistButtons.forEach(btn => {
                  const pid = btn.getAttribute("data-product-id");
                  if (pid && wishlistProductIds.includes(pid)) {
                    btn.classList.add("active");
                    updateWishlistLabel(btn);
                  }
                });
              }
            })
            .catch(() => null)
          );
        }

        // Collections active-state
        if (customerId && collectionIds.length > 0) {
          promises.push(
            fetch(`${API_BASE_URL}/customer-collection.php`, {
              method: "GET",
              headers: { "Content-Type": "application/json" }
            })
            .then(response => {
              if (response.status === 429) return null;
              return response.json();
            })
            .then(data => {
              if (!data) return;
              if (data && data.success && data.wishlists) {
                const wishlistCollectionIds = new Set();
                data.wishlists.forEach(item => {
                  if (item.collection && item.collection.shopify_collection_id) {
                    wishlistCollectionIds.add(item.collection.shopify_collection_id.toString());
                  }
                });
                wishlistButtons.forEach(btn => {
                  const cid = btn.getAttribute("data-collection-id");
                  if (cid && wishlistCollectionIds.has(cid)) {
                    btn.classList.add("active");
                    updateWishlistLabel(btn);
                  }
                });
              }
            })
            .catch(() => null)
          );
        }

        Promise.allSettled(promises).finally(() => {
          window.wishlistDataLoaded = true;
          isCheckingWishlist = false;
        });
      }, 500); // 0.5 second delay (reduced for better UX)
    }

    // Always re-check active-state for newly added buttons
    if (customerId) {
      window.wishlistChecked = false;
      window.wishlistDataLoaded = false;
      lastWishlistCheck = 0;
      isCheckingWishlist = false;
      checkExistingWishlist();
    }

    // Debounced wishlist counts (force reset keys for new buttons)
    scheduleCountsRefresh(true, 600);
  };

  // Public hook to force counts refresh externally if needed
  window.refreshWishlistCounts = function refreshWishlistCounts() {
    scheduleCountsRefresh(true, 500);
  };

  // Function to refresh wishlist count for a specific collection after add/remove operations
  function refreshCollectionWishlistCount(collectionId, change) {
    const countElement = document.querySelector(`.wishlist-count[data-collection-id="${collectionId}"] .count-number`);
    if (!countElement) return;

    // Add loading state
    countElement.parentElement.classList.add('loading');
    countElement.parentElement.classList.remove('loaded');

    // Optimistic update for immediate feedback
    const currentCount = parseInt(countElement.textContent) || '';
    const newCount = Math.max(0, currentCount + change);

    countElement.textContent = newCount;
    countElement.parentElement.classList.remove('loading');
    countElement.parentElement.classList.add('loaded');

    // Then fetch the real count from server after a short delay
    setTimeout(() => {
      fetch(`${API_BASE_URL}/collections-counts.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          collection_ids: [collectionId]
        })
      })
      .then(response => {
        if (response.status === 429) {
          return null;
        }
        return response.json();
      })
      .then(data => {
        if (!data) return;

        if (data.success && data.counts && data.counts.length > 0) {
          const collectionData = data.counts[0];
          const realCount = collectionData.wishlist_count || 0;

          // Update the display with real count
          countElement.textContent = realCount;
          countElement.parentElement.classList.remove('loading');
          countElement.parentElement.classList.add('loaded');
        }
      })
      .catch(error => {
      });
    }, 500); // 0.5 second delay to allow server processing (reduced for better UX)
  }

  // Function to refresh wishlist count for a specific product after add/remove operations
  function refreshProductWishlistCount(productId, change) {
    const countElement = document.querySelector(`.wishlist-count[data-product-id="${productId}"] .count-number`);
    if (!countElement) return;

    // Add loading state
    countElement.parentElement.classList.add('loading');
    countElement.parentElement.classList.remove('loaded');

    // Optimistic update
    const currentCount = parseInt(countElement.textContent) || 0;
    const newCount = Math.max(0, currentCount + change);
    countElement.textContent = newCount;
    countElement.parentElement.classList.remove('loading');
    countElement.parentElement.classList.add('loaded');

    // Fetch real count
    setTimeout(() => {
      fetch(`${API_BASE_URL}/products-counts.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          product_ids: [productId]
        })
      })
      .then(response => {
        if (response.status === 429) {
          return null;
        }
        return response.json();
      })
      .then(data => {
        if (!data) return;
        if (data.success && data.counts && data.counts.length > 0) {
          const productData = data.counts[0];
          const realCount = productData.wishlist_count || 0;
          countElement.textContent = realCount;
          countElement.parentElement.classList.remove('loading');
          countElement.parentElement.classList.add('loaded');
        }
      })
      .catch(() => {});
    }, 500);
  }

  // UNIVERSAL: Tracking function for universal wishlist (detects type automatically)
  function trackUniversalWishlist(button) {
    const productId = button.getAttribute("data-product-id");
    const collectionId = button.getAttribute("data-collection-id");
    const isActive = button.classList.contains('active');

    if (typeof gtag !== 'undefined') {
      if (productId) {
        // Track product wishlist
        const productTitle = button.getAttribute("data-product-title");
        const productHandle = button.getAttribute("data-product-handle");
        gtag('event', isActive ? 'product_remove_from_wishlist' : 'product_add_to_wishlist', {
          item_id: productId,
          item_name: productTitle,
          item_handle: productHandle
        });
      } else if (collectionId) {
        // Track collection wishlist
        const collectionName = button.getAttribute("data-collection-name");
        const collectionHandle = button.getAttribute("data-collection-handle");
        gtag('event', isActive ? 'auction_remove_from_wishlist' : 'auction_add_to_wishlist', {
          item_id: collectionId,
          item_name: collectionName,
          item_handle: collectionHandle
        });
      }
    }
  }

  // Initial wiring on first load
  document.addEventListener("DOMContentLoaded", function() {
    // If the user just came back from Shopify auth with a stashed intent,
    // finish the add now. Runs before initWishlistButtons() so the matching
    // heart is marked active on the same paint as the count refresh.
    resumePendingWishlist();

    scheduleCountsRefresh(false, 600);
    window.initWishlistButtons();
  });
})();
